import { Textarea } from "@/components/ui/textarea";
import update from "immutability-helper";
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Exercise, Workout } from "./workoutsColumns";
import type { EditorCommitOptions } from "@/lib/hooks/useEditorHistory";
import { Tables } from "@/lib/supabase/supabaseTypes";
import ExerciseDialog from "../exercises/exerciseDialog";
import ExerciseDetailDialog from "./ExerciseDetailDialog";
import InputFieldEditButton from "../inputFieldEditButton";
import {
  useDndContext,
  useDraggable,
  useDroppable,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import ExerciseRow from "./exerciseRow";
import { Button } from "../ui/button";
import {
  Dumbbell,
  Link,
  MoreHorizontal,
  PlusIcon,
  Trash2,
  Upload,
} from "lucide-react";
import ExerciseSelectDialog from "./exerciseSelectDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import SaveToLibraryDialog from "./saveToLibraryDialog";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils/ui";
import { motion } from "framer-motion";
import { useWorkoutEditorWorkspace } from "./WorkoutEditorWorkspace";
import {
  DROP_ANIMATION_DURATION_MS,
  ExerciseDragData,
  getGroupDragId,
  getGroupKey,
  GroupDragData,
  SectionDragData,
  WorkoutDropZoneData,
} from "./workoutEditorDnd";
import WorkoutSectionHeader from "./WorkoutSectionHeader";
import { createWorkoutExercise } from "./workoutEditorUtils";
import {
  TRAILING_LOOSE_SECTION_ID,
  WORKOUT_SECTION_LABELS,
  createDefaultSection,
  ensureWorkoutSections,
  groupsForSection,
  insertIndexForSection,
  isLooseSection,
} from "@/lib/utils/workoutSections";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { useVersionHistoryPreview } from "@/components/planHistory/VersionHistoryHost";

const WORKOUT_LAYOUT_TRANSITION = {
  duration: DROP_ANIMATION_DURATION_MS / 1000,
  ease: [0.22, 1, 0.36, 1] as const,
};
const WORKOUT_NOTES_MAX_HEIGHT_PX = 224;

type Value = {
  value: string;
  valueChange: (value: string) => void;
};

interface EditWorkoutViewProps {
  workout: Workout;
  updateWorkout: (workout: Workout, options?: EditorCommitOptions) => void;
  name: Value;
  description: Value;
  hasChanges: boolean;
  setHasChanges: (hasChanges: boolean) => void;
  removeExercise: (Exercise: Exercise) => void;
  template?: boolean;
  deleteWorkout?: () => void;
  readOnly?: boolean;
  clientId?: string;
  hideName?: boolean;
}

type WorkoutDropZoneProps = {
  id: UniqueIdentifier;
  data: WorkoutDropZoneData;
  enabled: boolean;
  compact?: boolean;
  hidden?: boolean;
  reserveSpace?: boolean;
  inLinkSlot?: boolean;
};

const WorkoutDropZone = ({
  id,
  data,
  enabled,
  compact = false,
  hidden = false,
  reserveSpace = false,
  inLinkSlot = false,
}: WorkoutDropZoneProps) => {
  if (hidden) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none relative overflow-visible",

          compact ? "px-2" : "",
          inLinkSlot
            ? "h-10 w-full"
            : reserveSpace
              ? "my-0 h-10 opacity-0"
              : "my-0 h-0 opacity-0",
        )}
      />
    );
  }

  const { setNodeRef } = useDroppable({
    id,
    data,
    disabled: !enabled,
  });
  const { active, over } = useDndContext();
  const isDragging = Boolean(active) && enabled;
  const isOver = over?.id === id;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isOver) {
      setIsExpanded(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsExpanded(true);
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
      setIsExpanded(false);
    };
  }, [isOver]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "pointer-events-none relative overflow-visible",
        isDragging
          ? "transition-all duration-200 ease-out"
          : "transition-none",
        compact ? "px-2" : "",
        inLinkSlot
          ? ["h-10 w-full", isOver && (isExpanded ? "h-16" : "h-10")]
          : [
              isDragging
                ? reserveSpace
                  ? "my-0 h-10 opacity-0"
                  : compact
                    ? "my-1 h-4 opacity-0"
                    : "my-1.5 h-5 opacity-0"
                : "my-0 h-0 opacity-0",
              isOver &&
                (isExpanded
                  ? compact
                    ? "my-2 h-8 opacity-100"
                    : "my-2 h-10 opacity-100"
                  : compact
                    ? "my-1 h-4 opacity-100"
                    : "my-1.5 h-5 opacity-100"),
            ],
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-1/2 flex select-none -translate-y-1/2 items-center justify-center rounded-lg border border-dashed text-[10px] font-semibold uppercase tracking-[0.2em] transition-all duration-200",
          inLinkSlot
            ? isOver
              ? isExpanded
                ? "h-12 px-4"
                : "h-10 px-4"
              : "inset-0 h-full translate-y-0 px-4"
            : isOver
              ? compact
                ? isExpanded
                  ? "h-8 px-3"
                  : "h-4 px-3"
                : isExpanded
                  ? "h-10 px-4"
                  : "h-5 px-4"
              : compact
                ? "h-4 px-3"
                : "h-5 px-4",
          isOver
            ? "border-primary/70 bg-primary/10 text-primary opacity-100"
            : "border-transparent bg-transparent text-primary opacity-0",
        )}
      >
        <span
          className={cn(
            "pointer-events-none transition-all duration-150",
            isExpanded
              ? "translate-y-0 opacity-100 delay-75"
              : "translate-y-0.5 opacity-0 delay-0",
          )}
        >
          {data.type === "superset-insert"
            ? "Drop into superset"
            : data.type === "section-insert"
              ? "Drop section"
              : "Drop here"}
        </span>
      </div>
    </div>
  );
};

type SupersetShellProps = {
  id: string;
  data: GroupDragData;
  disabled: boolean;
  isDragSource: boolean;
  hasLinkBelow: boolean;
  isRecentlyDropped?: boolean;
  children: React.ReactNode;
};

const SupersetShell = ({
  id,
  data,
  disabled,
  isDragSource,
  hasLinkBelow,
  isRecentlyDropped = false,
  children,
}: SupersetShellProps) => {
  const { setNodeRef, listeners, attributes } = useDraggable({
    id,
    data,
    disabled,
  });

  return (
    <motion.div
      initial={false}
      animate={
        isRecentlyDropped
          ? { opacity: [0.45, 1] }
          : { opacity: isDragSource ? 0.3 : 1 }
      }
      transition={WORKOUT_LAYOUT_TRANSITION}
    >
      <Card
        ref={setNodeRef}
        {...(!disabled ? listeners : {})}
        {...(!disabled ? attributes : {})}
        className={cn(
          "rounded-2xl border-primary/20 bg-primary/5 p-3 transition-[color,background-color,border-color,opacity,box-shadow] duration-200 select-none touch-manipulation",
          !hasLinkBelow && "mb-3",
          !disabled && "cursor-grab active:cursor-grabbing",
          isDragSource && "border-foreground/20 bg-muted/70 opacity-45",
        )}
      >
        <div className="mb-3 flex items-center">
          <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-primary">
            Superset
          </span>
        </div>
        <div data-no-drag="true">{children}</div>
      </Card>
    </motion.div>
  );
};

type SectionShellProps = {
  id: string;
  data: SectionDragData;
  disabled: boolean;
  isDragSource: boolean;
  isDropTarget: boolean;
  isRecentlyDropped?: boolean;
  label: string;
  children: React.ReactNode;
};

const SectionShell = ({
  id,
  data,
  disabled,
  isDragSource,
  isDropTarget,
  isRecentlyDropped = false,
  label,
  children,
}: SectionShellProps) => {
  const { setNodeRef: setDragRef, listeners, attributes } = useDraggable({
    id,
    data,
    disabled,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `section-append-${data.sectionId}`,
    data: {
      type: "section-append",
      sectionId: data.sectionId,
    },
    disabled: !isDropTarget,
  });

  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  return (
    <motion.div
      initial={false}
      animate={
        isRecentlyDropped
          ? { opacity: [0.45, 1] }
          : { opacity: isDragSource ? 0.3 : 1 }
      }
      transition={WORKOUT_LAYOUT_TRANSITION}
    >
      <Card
        ref={setNodeRef}
        className={cn(
          "mb-8 mt-4 rounded-2xl border-border bg-muted/40 p-3 transition-[color,background-color,border-color,opacity,box-shadow] duration-200",
          !disabled && "select-none",
          isDragSource && "border-foreground/20 bg-muted/70 opacity-45",
          isOver && isDropTarget && "border-primary/50 bg-primary/5 ring-2 ring-primary/30",
        )}
      >
        <div
          className={cn(
            "mb-3 flex items-center",
            !disabled && "cursor-grab active:cursor-grabbing",
          )}
          {...(!disabled ? listeners : {})}
          {...(!disabled ? attributes : {})}
        >
          <span className="rounded-full bg-foreground/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-foreground/80">
            {label}
          </span>
        </div>
        <div
          onPointerDown={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      </Card>
    </motion.div>
  );
};

const EditWorkoutView: FunctionComponent<EditWorkoutViewProps> = ({
  workout,
  updateWorkout,
  name,
  description,
  setHasChanges,
  removeExercise,
  template = false,
  deleteWorkout,
  readOnly: readOnlyProp = false,
  clientId,
  hideName = false,
}) => {
  const isPreviewing = useVersionHistoryPreview();
  const readOnly = readOnlyProp || isPreviewing;
  const { activeDrag, recentDrop } = useWorkoutEditorWorkspace();
  const org = useOrg();
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const [openExerciseDialog, setOpenExerciseDialog] = useState(false);
  const [openExerciseSelectDialog, setOpenExerciseSelectDialog] =
    useState(false);
  const [openSaveLibraryDialog, setopenSaveLibraryDialog] = useState<Workout>();
  const [replacingExercise, setReplacingExercise] = useState<
    Exercise | undefined
  >();
  const [exerciseToView, setExerciseToView] = useState<Exercise>();
  const [exerciseDetail, setExerciseDetail] = useState<Exercise | null>(null);
  const [targetSectionId, setTargetSectionId] = useState<string | null>(null);

  const editorWorkout = useMemo(
    () => ensureWorkoutSections(workout),
    [workout],
  );

  const { data: clientUnit } = trpc.clients.get.useQuery(
    { clientId: clientId ?? "" },
    { enabled: Boolean(clientId) },
  );

  const weightUnitLabel =
    clientUnit?.weight_unit ?? org?.weightUnitDefault ?? "kg";
  const distanceUnitLabel =
    clientUnit?.distance_unit === "mi"
      ? "mi"
      : clientUnit?.distance_unit === "km"
        ? "km"
        : org?.distanceUnitDefault === "mi"
          ? "mi"
          : "km";

  const handleExerciseDialogOpenChange = (open: boolean) => {
    setOpenExerciseDialog(open);
    setExerciseToView(undefined);
  };

  const updateExercise = useCallback(
    (exercise: Exercise, ex_order: number, sub_order: number) => {
      const newExercises = [...workout.exerciseCollection];
      newExercises[ex_order][sub_order] = exercise;
      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout, { coalesceKey: `exercise:${exercise.id}` });
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const addExercise = (exercise: Tables<"exercises">, sectionId?: string) => {
    const ensured = ensureWorkoutSections(workout);
    const requested = sectionId ?? targetSectionId ?? undefined;
    let sections = ensured.sections;
    const last = sections[sections.length - 1];
    const wantsTrailing = requested === TRAILING_LOOSE_SECTION_ID;
    let resolvedSectionId: string | undefined;

    if (wantsTrailing || (!requested && last && !isLooseSection(last))) {
      const section = createDefaultSection({
        is_default: false,
        position: sections.length + 1,
      });
      sections = [...sections, section];
      resolvedSectionId = section.id;
    } else if (requested && requested !== TRAILING_LOOSE_SECTION_ID) {
      resolvedSectionId = requested;
    } else {
      resolvedSectionId = last?.id;
    }

    const localIndex = resolvedSectionId
      ? groupsForSection({ ...ensured, sections }, resolvedSectionId).length
      : ensured.exerciseCollection.length;
    const insertAt = resolvedSectionId
      ? insertIndexForSection({ ...ensured, sections }, resolvedSectionId, localIndex)
      : ensured.exerciseCollection.length;
    const newExercise = createWorkoutExercise({
      exercise,
      exOrder: insertAt,
      subOrder: 0,
      sectionId: resolvedSectionId,
    });
    const nextCollection = [...ensured.exerciseCollection];
    nextCollection.splice(insertAt, 0, [newExercise]);
    nextCollection.forEach((group, groupIndex) => {
      group.forEach((item, subIndex) => {
        item.ex_order = groupIndex;
        item.sub_order = subIndex;
      });
    });
    updateWorkout({
      ...ensured,
      sections,
      exerciseCollection: nextCollection,
    });
    setHasChanges(true);
  };

  const removeFromSuperset = useCallback(
    (exercise: Exercise) => {
      const newExercises = [...workout.exerciseCollection];
      newExercises[exercise.ex_order] = newExercises[exercise.ex_order]
        .filter((ex) => ex.id !== exercise.id)
        .map((ex, i) => {
          return { ...ex, sub_order: i };
        });

      if (exercise.sub_order === 0) {
        newExercises.splice(exercise.ex_order, 0, [exercise]);
      } else {
        newExercises.splice(exercise.ex_order + 1, 0, [exercise]);
      }

      newExercises.forEach((ex, i) => {
        ex.forEach((e, j) => {
          e.ex_order = i;
          e.sub_order = j;
        });
      });

      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const addToSuperset = useCallback(
    (nextExercise: Exercise, cur_order: number, cur_sub_order: number) => {
      const newExercises = [...workout.exerciseCollection];
      const currentGroup = newExercises[cur_order];
      const nextGroup = newExercises[nextExercise.ex_order];
      const nextGroupIsSuperset = nextGroup.length > 1;

      if (nextGroupIsSuperset) {
        const [exerciseToMove] = currentGroup.splice(cur_sub_order, 1);
        if (!exerciseToMove) {
          return;
        }

        nextGroup.push({
          ...exerciseToMove,
          ex_order: nextExercise.ex_order,
          sub_order: nextGroup.length,
        });

        if (currentGroup.length === 0) {
          newExercises.splice(cur_order, 1);
        }
      } else {
        const newExercise: Exercise = {
          ...nextExercise,
          ex_order: cur_order,
          sub_order: cur_sub_order + 1,
        };

        currentGroup.splice(cur_sub_order + 1, 0, newExercise);

        if (nextGroup.length === 1) {
          newExercises.splice(nextExercise.ex_order, 1);
        } else {
          nextGroup.splice(nextExercise.sub_order, 1);
        }
      }

      newExercises.forEach((ex, i) => {
        ex.forEach((e, j) => {
          e.ex_order = i;
          e.sub_order = j;
        });
      });

      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const replaceExercise = useCallback(
    (order: number, sub_order: number, exercise: Tables<"exercises">) => {
      const newExercises = [...workout.exerciseCollection];
      const oldExercise = newExercises[order][sub_order];
      newExercises[order][sub_order] = {
        ...oldExercise,
        ...exercise,
        ex_order: order,
        sub_order: sub_order,
      };
      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const handleExerciseSelectDialogOpenChange = (open: boolean) => {
    setOpenExerciseSelectDialog(open);
  };

  const handleExerciseSelected = (exercise: Tables<"exercises">) => {
    setOpenExerciseSelectDialog(false);

    if (replacingExercise) {
      replaceExercise(
        replacingExercise.ex_order,
        replacingExercise.sub_order,
        exercise,
      );
      setReplacingExercise(undefined);
      return;
    }
    addExercise(exercise, targetSectionId ?? undefined);
    setTargetSectionId(null);
  };

  const updateSection = (sectionId: string, next: Workout["sections"][number]) => {
    const ensured = ensureWorkoutSections(workout);
    updateWorkout({
      ...ensured,
      sections: ensured.sections.map((section) =>
        section.id === sectionId ? next : section,
      ),
    });
    setHasChanges(true);
  };

  const addSection = () => {
    const ensured = ensureWorkoutSections(workout);
    const wrappingCount = ensured.sections.filter(
      (section) => !isLooseSection(section),
    ).length;
    const section = createDefaultSection({
      is_default: false,
      position: ensured.sections.length + 1,
      name: wrappingCount === 0 ? "Section" : `Section ${wrappingCount + 1}`,
    });
    updateWorkout({
      ...ensured,
      sections: [...ensured.sections, section],
    });
    setHasChanges(true);
  };

  const deleteSection = (sectionId: string) => {
    const ensured = ensureWorkoutSections(workout);
    if (ensured.sections.length <= 1) return;
    const remaining = ensured.sections.filter((section) => section.id !== sectionId);
    updateWorkout({
      ...ensured,
      sections: remaining.map((section, index) => ({
        ...section,
        position: index + 1,
        is_default: index === 0,
      })),
      exerciseCollection: ensured.exerciseCollection.filter(
        (group) => (group[0]?.section_id ?? ensured.sections[0]?.id) !== sectionId,
      ).map((group, groupIndex) =>
        group.map((exercise, subIndex) => ({
          ...exercise,
          ex_order: groupIndex,
          sub_order: subIndex,
        })),
      ),
    });
    setHasChanges(true);
  };

  const handleExerciseCreated = (exercise: Tables<"exercises">) => {
    if (replacingExercise) {
      replaceExercise(
        replacingExercise.ex_order,
        replacingExercise.sub_order,
        exercise,
      );
      setReplacingExercise(undefined);
      return;
    }
    addExercise(exercise);
  };

  const hasExerciseDrag = activeDrag?.type === "exercise";
  const hasLibraryExerciseDrag = activeDrag?.type === "library-exercise";
  const hasGroupDrag = activeDrag?.type === "group";
  const hasSectionDrag = activeDrag?.type === "section";
  const showDropZones = !readOnly && Boolean(activeDrag);
  const groupInsertEnabled = showDropZones;
  const sectionInsertEnabled = showDropZones && hasSectionDrag;
  const supersetInsertEnabled =
    showDropZones &&
    (hasExerciseDrag || hasLibraryExerciseDrag) &&
    !hasSectionDrag;
  const hiddenGroupInsertIndices = new Set<number>();
  const sourceSectionId =
    activeDrag && "sourceSectionId" in activeDrag
      ? activeDrag.sourceSectionId
      : undefined;

  if (activeDrag?.type === "group") {
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex);
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex + 1);
  }

  if (
    activeDrag?.type === "exercise" &&
    workout.exerciseCollection[activeDrag.sourceGroupIndex]?.length === 1
  ) {
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex);
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex + 1);
  }

  const isGroupInsertHidden = (index: number, sectionId: string) => {
    if (activeDrag?.type === "section") {
      if (activeDrag.sectionId === sectionId) return true;
      const section = editorWorkout.sections.find((item) => item.id === sectionId);
      if (!section) return false;
      return !isLooseSection(section);
    }
    if (!hiddenGroupInsertIndices.has(index)) return false;
    if (sourceSectionId && sourceSectionId !== sectionId) return false;
    return true;
  };

  const isSupersetInsertHidden = (groupIndex: number, position: number) => {
    if (activeDrag?.type !== "exercise") {
      return false;
    }

    if (activeDrag.sourceGroupIndex !== groupIndex) {
      return false;
    }

    const sourceGroup = workout.exerciseCollection[groupIndex];
    if (!sourceGroup || sourceGroup.length <= 1) {
      return false;
    }

    return (
      position === activeDrag.sourceExerciseIndex ||
      position === activeDrag.sourceExerciseIndex + 1
    );
  };

  useEffect(() => {
    const textarea = descriptionRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      WORKOUT_NOTES_MAX_HEIGHT_PX,
    )}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > WORKOUT_NOTES_MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [description.value]);

  return (
    <>
      <ExerciseDialog
        open={openExerciseDialog}
        onOpenChange={handleExerciseDialogOpenChange}
        onExerciseCreated={handleExerciseCreated}
        exercise={exerciseToView}
      />
      <ExerciseSelectDialog
        open={openExerciseSelectDialog}
        onOpenChange={handleExerciseSelectDialogOpenChange}
        exerciseSelected={handleExerciseSelected}
        createExerciseSelected={() => {
          setOpenExerciseSelectDialog(false);
          setOpenExerciseDialog(true);
        }}
      />
      <SaveToLibraryDialog
        workout={openSaveLibraryDialog}
        onClose={() => {
          setopenSaveLibraryDialog(undefined);
        }}
      />
      <ExerciseDetailDialog
        open={Boolean(exerciseDetail)}
        exercise={exerciseDetail}
        clientId={clientId}
        weightUnitLabel={weightUnitLabel}
        distanceUnitLabel={distanceUnitLabel}
        showEditExerciseButton={!readOnly}
        onEditExercise={() => {
          if (!exerciseDetail) return;
          setExerciseToView(exerciseDetail);
          setExerciseDetail(null);
          setOpenExerciseDialog(true);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setExerciseDetail(null);
          }
        }}
        analyticsName="edit_workout_exercise_detail_dialog"
      />

      <div className="flex min-w-0 flex-col gap-2 overflow-visible">
        {(!hideName || !template) && (
          <div className="my-2 flex justify-between px-3 sm:px-0">
            {!hideName && (
              <InputFieldEditButton
                value={name.value}
                setValue={(value) => {
                  name.valueChange(value);
                  setHasChanges(true);
                }}
                placeholder={"New Training Plan"}
                className="text-2xl font-bold"
                disabled={readOnly}
              />
            )}
            {!template && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setopenSaveLibraryDialog(workout);
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Save to Library
                </DropdownMenuItem>
                {!readOnly ? (
                  <DropdownMenuItem
                    onClick={() => {
                      deleteWorkout?.();
                    }}
                    variant="destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Workout
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          </div>
        )}

        {!readOnly || description.value.trim().length > 0 ? (
          <div className="px-3 sm:px-0">
            <Textarea
              ref={descriptionRef}
              value={description.value}
              onChange={(e) => {
                description.valueChange(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Add notes..."
              disabled={readOnly}
              className="min-h-[96px] resize-none overflow-y-hidden border-0 bg-muted/60 shadow-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
            />
          </div>
        ) : null}

        <div className="mb-10 mt-4 px-3 pb-32 sm:px-0">
          {editorWorkout.exerciseCollection.length === 0 &&
            editorWorkout.sections.length <= 1 && (
            <Card className="flex h-full w-full flex-col items-center justify-center p-4">
              <Dumbbell className="my-4 h-10 w-10 text-primary" />
              <h1 className="font-medium">No Exercises</h1>
              <p className="mb-4 text-gray-500">
                No exercises have been added to this workout yet.
              </p>
            </Card>
          )}

          <div className="space-y-0">
            {editorWorkout.sections.map((section, sectionIndex) => {
              const sectionGroups = groupsForSection(editorWorkout, section.id);
              const wrapped = !isLooseSection(section);
              const showAddExercise = !readOnly && wrapped;
              const sectionDragData: SectionDragData = {
                type: "section",
                sectionId: section.id,
                sectionName:
                  section.name?.trim() ||
                  WORKOUT_SECTION_LABELS[section.section_type] ||
                  "Section",
                sourceSectionIndex: sectionIndex,
                groupCount: sectionGroups.length,
              };
              const isSectionDropTarget =
                wrapped &&
                showDropZones &&
                !hasSectionDrag &&
                (hasLibraryExerciseDrag ||
                  ((hasExerciseDrag || hasGroupDrag) &&
                    sourceSectionId !== section.id));
              const hideSectionInsert =
                hasSectionDrag &&
                (activeDrag.sectionId === section.id ||
                  editorWorkout.sections[sectionIndex - 1]?.id ===
                    activeDrag.sectionId);
              const sectionBody = (
                <>
                  {wrapped ? (
                    <WorkoutSectionHeader
                      section={section}
                      sectionIndex={sectionIndex}
                      canDelete={editorWorkout.sections.length > 1}
                      readOnly={readOnly}
                      onChange={(next) => updateSection(section.id, next)}
                      onDelete={() => deleteSection(section.id)}
                    />
                  ) : null}

                  {sectionGroups.length === 0 ? (
                    <WorkoutDropZone
                      id={`group-insert-section-${section.id}-0`}
                      data={{
                        type: "group-insert",
                        index: insertIndexForSection(
                          editorWorkout,
                          section.id,
                          0,
                        ),
                        sectionId: section.id,
                      }}
                      enabled={groupInsertEnabled}
                      hidden={isGroupInsertHidden(
                        insertIndexForSection(editorWorkout, section.id, 0),
                        section.id,
                      )}
                    />
                  ) : null}

                  {sectionGroups.map(({ group, globalIndex: index }, localIndex) => {
              const groupLength = group.length;
              const isSuperset = groupLength > 1;
              const groupKey = getGroupKey(group);
              const groupDragId = getGroupDragId(group);
              const groupDragData: GroupDragData = {
                type: "group",
                groupId: groupDragId,
                exerciseCount: group.length,
                sourceGroupIndex: index,
                sourceSectionId: section.id,
              };
              const isActiveGroupDrag =
                hasGroupDrag && activeDrag?.groupId === groupDragId;
              const nextInSection = sectionGroups[localIndex + 1];
              const showSupersetLink = Boolean(nextInSection);
              const lastExerciseInGroup = group[groupLength - 1];

              const groupRows = (
                <div>
                  {isSuperset && (
                    <WorkoutDropZone
                      id={`superset-insert-${index}-0`}
                      data={{
                        type: "superset-insert",
                        groupIndex: index,
                        position: 0,
                        sectionId: section.id,
                      }}
                      enabled={supersetInsertEnabled}
                      compact
                      hidden={isSupersetInsertHidden(index, 0)}
                    />
                  )}

                  {group.map((exercise, sub_index) => {
                    const canShowLink =
                      !isSuperset &&
                      sub_index === groupLength - 1 &&
                      showSupersetLink;
                    const exerciseDragData: ExerciseDragData = {
                      type: "exercise",
                      exerciseId: exercise.sortableId,
                      exerciseName: exercise.name,
                      sourceGroupIndex: index,
                      sourceExerciseIndex: sub_index,
                      sourceSectionId: section.id,
                    };

                    return (
                      <div key={exercise.sortableId}>
                        <ExerciseRow
                          exercise={exercise}
                          readOnly={readOnly}
                          updateExercise={updateExercise}
                          removeExercise={removeExercise}
                          replaceExercise={(exercise) => {
                            if (readOnly) return;
                            setReplacingExercise(exercise);
                            setOpenExerciseSelectDialog(true);
                          }}
                          exerciseSelected={(exercise) => {
                            setExerciseDetail(exercise);
                          }}
                          groupLength={groupLength}
                          removeFromSuperset={removeFromSuperset}
                          canShowLink={canShowLink}
                          dragId={`exercise-${exercise.sortableId}`}
                          dragData={exerciseDragData}
                          dragDisabled={readOnly || isActiveGroupDrag}
                          isRecentlyDropped={
                            recentDrop?.type === "exercise" &&
                            recentDrop.exerciseId === exercise.sortableId
                          }
                          clientId={clientId}
                          weightUnitLabel={weightUnitLabel}
                          distanceUnitLabel={distanceUnitLabel}
                        />
                        {isSuperset && (
                          <WorkoutDropZone
                            id={`superset-insert-${index}-${sub_index + 1}`}
                            data={{
                              type: "superset-insert",
                              groupIndex: index,
                              position: sub_index + 1,
                              sectionId: section.id,
                            }}
                            enabled={supersetInsertEnabled}
                            compact
                            hidden={isSupersetInsertHidden(
                              index,
                              sub_index + 1,
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <div key={groupKey}>
                  {localIndex === 0 ? (
                    <WorkoutDropZone
                      id={`group-insert-${index}`}
                      data={{
                        type: "group-insert",
                        index,
                        sectionId: section.id,
                      }}
                      enabled={groupInsertEnabled}
                      hidden={isGroupInsertHidden(index, section.id)}
                    />
                  ) : null}
                  {isSuperset ? (
                    <SupersetShell
                      id={groupDragId}
                      data={groupDragData}
                      disabled={readOnly}
                      isDragSource={isActiveGroupDrag}
                      hasLinkBelow={showSupersetLink && !readOnly}
                      isRecentlyDropped={
                        recentDrop?.type === "group" &&
                        recentDrop.groupId === groupDragId
                      }
                    >
                      {groupRows}
                    </SupersetShell>
                  ) : (
                    groupRows
                  )}

                  {showSupersetLink && !readOnly ? (
                    <div className="relative" key={`${groupKey}-between`}>
                      <div
                        className={cn(
                          "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
                          activeDrag && "pointer-events-none opacity-0",
                        )}
                        aria-hidden={activeDrag ? "true" : undefined}
                      >
                        <Button
                          variant="ghost"
                          tabIndex={activeDrag ? -1 : undefined}
                          onClick={() => {
                            const nextExercise = nextInSection.group[0];
                            addToSuperset(
                              nextExercise,
                              lastExerciseInGroup.ex_order,
                              lastExerciseInGroup.sub_order,
                            );
                          }}
                        >
                          <Link />
                        </Button>
                      </div>
                      <WorkoutDropZone
                        id={`group-insert-${index + 1}`}
                        data={{
                          type: "group-insert",
                          index: index + 1,
                          sectionId: section.id,
                        }}
                        enabled={groupInsertEnabled}
                        hidden={isGroupInsertHidden(index + 1, section.id)}
                        inLinkSlot
                      />
                    </div>
                  ) : (
                    <WorkoutDropZone
                      id={`group-insert-${section.id}-${index + 1}`}
                      data={{
                        type: "group-insert",
                        index: index + 1,
                        sectionId: section.id,
                      }}
                      enabled={groupInsertEnabled}
                      hidden={isGroupInsertHidden(index + 1, section.id)}
                      reserveSpace={false}
                    />
                  )}
                </div>
              );
                  })}

                  {showAddExercise ? (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        className="inline-flex items-center justify-center gap-2"
                        onClick={() => {
                          setTargetSectionId(section.id);
                          setOpenExerciseSelectDialog(true);
                        }}
                      >
                        <PlusIcon className="h-4 w-4 shrink-0 opacity-50" />
                        Add Exercise
                      </Button>
                    </div>
                  ) : null}
                </>
              );

              return (
                <div key={section.id}>
                  <WorkoutDropZone
                    id={`section-insert-${section.id}`}
                    data={{
                      type: "section-insert",
                      sectionIndex,
                    }}
                    enabled={sectionInsertEnabled}
                    hidden={hideSectionInsert}
                    reserveSpace
                  />
                  {wrapped ? (
                    <SectionShell
                      id={`section-${section.id}`}
                      data={sectionDragData}
                      disabled={readOnly}
                      isDragSource={
                        hasSectionDrag && activeDrag.sectionId === section.id
                      }
                      isDropTarget={isSectionDropTarget}
                      isRecentlyDropped={
                        recentDrop?.type === "section" &&
                        recentDrop.sectionId === section.id
                      }
                      label={sectionDragData.sectionName}
                    >
                      {sectionBody}
                    </SectionShell>
                  ) : (
                    <div className="space-y-0">{sectionBody}</div>
                  )}
                </div>
              );
            })}
          </div>

          {showDropZones &&
          editorWorkout.sections.length > 0 &&
          !isLooseSection(
            editorWorkout.sections[editorWorkout.sections.length - 1],
          ) ? (
            <WorkoutDropZone
              id="group-insert-trailing-loose"
              data={{
                type: "group-insert",
                index: editorWorkout.exerciseCollection.length,
                sectionId: TRAILING_LOOSE_SECTION_ID,
              }}
              enabled={groupInsertEnabled && !hasSectionDrag}
              reserveSpace
            />
          ) : null}

          <WorkoutDropZone
            id="section-insert-end"
            data={{
              type: "section-insert",
              sectionIndex: editorWorkout.sections.length,
            }}
            enabled={sectionInsertEnabled}
            hidden={
              hasSectionDrag &&
              editorWorkout.sections[editorWorkout.sections.length - 1]?.id ===
                activeDrag.sectionId
            }
            reserveSpace
          />

          {!readOnly ? (
            <div className="mt-8 flex justify-center gap-3">
              <Button
                variant="outline"
                className="inline-flex items-center justify-center gap-2"
                onClick={() => {
                  setTargetSectionId(null);
                  setOpenExerciseSelectDialog(true);
                }}
              >
                <PlusIcon className="h-4 w-4 shrink-0 opacity-50" />
                Add Exercise
              </Button>
              <Button
                variant="outline"
                className="inline-flex items-center justify-center gap-2"
                onClick={addSection}
              >
                <PlusIcon className="h-4 w-4 shrink-0 opacity-50" />
                Add Section
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default EditWorkoutView;
